import {useCallback, useMemo} from "react";
import {OptionProps, SingleValue, MultiValue, components, MultiValueGenericProps} from "react-select";
import {useTranslation} from "react-i18next";

import {Competence} from "../../schema";
import CreatableSelect, {Option} from "../../ui/creatableselect";
import {useAddCompetenceMutation, useUpdateCompetenceMutation} from "./CompetenciesCreatableSelect.graphql";
import {useModal} from "../ModalProvider";
import CompetenceModal from "./CompetenceModal";
import IconButton from "../../ui/iconbutton";
import {EditIcon} from "../../ui/icons";

type MultiProps = {
  isMulti: true;
  currentItemIds?: string[];
  onSelect?: (ids?: string[]) => void;
};

type SingleProps = {
  isMulti: false;
  currentItemIds?: string;
  onSelect?: (id?: string) => void;
};

type Props = (MultiProps | SingleProps) & {
  competencies: Competence[];
  disabled?: boolean;
};

export default function CompetenciesCreatableSelect(props: Props) {

  const {
    competencies,
    disabled,
    currentItemIds,
    isMulti,
    ...restProps
  } = props;
  const {t} = useTranslation();
  const [applyAddCompetenceMutation] = useAddCompetenceMutation();
  const [applyUpdateCompetenceMutation] = useUpdateCompetenceMutation();
  const {add: addModal} = useModal();

  const handleCreate = useCallback(
    (label: string) => {
      const modal = addModal(
        {
          header: <CompetenceModal.Header />,
          content: (
            <CompetenceModal.Content
              initialName={label}
              onSave={(data) => {
                applyAddCompetenceMutation({
                  variables: {
                    name: data.name,
                    description: data.description || null,
                    indicators: data.indicators || [],
                  },
                  refetchQueries: ["EditorEditChallengeSceneCompetenciesQuery"],
                  awaitRefetchQueries: true,
                }).then((result) => {
                  if (result.data && props.onSelect) {
                    if (isMulti) {
                      const existingIds = Array.isArray(currentItemIds)
                        ? currentItemIds : (currentItemIds ? [currentItemIds] : []);
                      const newIds = [...existingIds, result.data.competence.id];
                      props.onSelect?.(newIds)
                    } else {
                      props.onSelect?.(result.data.competence.id)
                    }
                  }
                  modal.remove();
                });
              }}
            />
          ),
        },
        true,
      );
    },
    [addModal, applyAddCompetenceMutation, props, isMulti, currentItemIds],
  );

  const handleEdit = useCallback(
    (competenceId: string, e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      const competence = competencies.find((c) => c.id === competenceId);
      if (!competence) return;

      const modal = addModal(
        {
          header: <CompetenceModal.Header />,
          content: (
            <CompetenceModal.Content
              competence={competence}
              onSave={(data) => {
                applyUpdateCompetenceMutation({
                  variables: {
                    id: competenceId,
                    name: data.name,
                    description: data.description || null,
                    indicators: data.indicators || [],
                  },
                  refetchQueries: ["EditorEditChallengeSceneCompetenciesQuery"],
                  awaitRefetchQueries: true,
                }).then(() => {
                  modal.remove();
                });
              }}
            />
          ),
        },
        true,
      );
    },
    [addModal, applyUpdateCompetenceMutation, competencies],
  );

  const handleChange = useCallback(
    (data: SingleValue<Option> | MultiValue<Option>) => {
      if (!props.onSelect) return;

      if (isMulti) {
        if (Array.isArray(data)) {
          const ids = data.map((item: Option) => item.value).filter((id): id is string => !!id);
          props.onSelect(ids)
        } else {
          props.onSelect([])
        }
      } else {
        if (data && "value" in data && data.value) {
          props.onSelect(data.value)
        } else {
          props.onSelect(undefined)
        }
      }
    },
    [props, isMulti],
  );

  const competenceOptions = useMemo(() => {
    return competencies?.map((competence) => ({
      value: competence.id,
      label: competence.name,
    }));
  }, [competencies]);

  const currentItemOptions = useMemo(() => {
    if (!currentItemIds) return [];

    if (isMulti) {
      const idsArray = Array.isArray(currentItemIds) ? currentItemIds : [];
      return competenceOptions.filter((option) => idsArray.includes(option.value));
    } else {
      const singleId = typeof currentItemIds === "string" ?
        currentItemIds : (Array.isArray(currentItemIds) ? currentItemIds[0] : undefined);
      return singleId ? competenceOptions.filter((option) => option.value === singleId) : [];
    }
  }, [currentItemIds, competenceOptions, isMulti]);
  const CustomOption = (props: OptionProps<Option, boolean>) => {
    const {innerProps, data, isSelected, getStyles} = props;
    const competenceId = data.value;

    const defaultStyles = getStyles("option", props);

    return (
      <div
        {...innerProps}
        style={{
          ...defaultStyles,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          cursor: "pointer",
        }}
      >
        <span>{data.label}</span>
        {competenceId && (
          <IconButton
            icon={EditIcon}
            size="s"
            title={t("common.edit")}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleEdit(competenceId, e)}
            style={{
              marginLeft: "8px",
              color: isSelected ? "white" : "var(--text-color-regular)",
            }}
          />
        )}
      </div>
    );
  };
  const CustomMultiValueLabel = (props: MultiValueGenericProps<Option, boolean>) => {
    const {data} = props;
    const competenceId = data.value;

    return (
      <>
        <components.MultiValueLabel {...props}>
          {data.label}
        </components.MultiValueLabel>
        {competenceId && (
          <IconButton
            icon={EditIcon}
            size="s"
            title={t("common.edit")}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleEdit(competenceId, e);
            }}
          />
        )}
      </>
    );
  };
  return (
    <CreatableSelect
      isMulti={isMulti}
      options={competenceOptions}
      value={currentItemOptions}
      disabled={disabled}
      onChange={handleChange}
      onCreate={handleCreate}
      components={isMulti ? {
        Option: CustomOption,
        MultiValueLabel: CustomMultiValueLabel
      } : {}}
      noOptionsMessage={()=>t("components.CompetenciesCreatableSelect.noOptions")}
      {...restProps}
    />
  );
}
